/**
 * @class Quickshop
 * @classdesc The quickshop for a theme that will show a product within it dynamically
 *
 * @extends Core.Component
 */
export default class Quickshop extends Core.Component {
  constructor(el, context) {
    super(el, context);
    this.loaded = false;
    this.loadedLink = '';
  }

  onMount() {
    this.drawerModal = Core.Components.getComponent('drawer-modal');

    if (!this.drawerModal) return;

    this.drawerFlyout = this.drawerModal.createFlyout();
  }

  onDismount() {
    super.onDismount();

    if (this.drawerFlyout) {
      this.drawerFlyout.then(drawerFlyout => drawerFlyout.destroy());
    }
  }

  /**
   * Opens a quickshop using a drawer modal
   *
   * @param {HTMLNode} trigger The trigger that was clicked to open the quickshop
   * @param {Object} data An object containing data relevant to the quickshop
   * @param {String} data.productLink The link to fetch the product quickshop content
   */
  async open(trigger, data) {
    if (!this.drawerFlyout) return;

    const drawerFlyout = await this.drawerFlyout;

    this.drawerModal.clearFlyout(drawerFlyout);
    this.drawerModal.openFlyout(drawerFlyout, trigger);

    if (!this.loaded || this.loadedLink !== data.productLink) {
      this.loadQuickshop(data)
        .then(() => {
          drawerFlyout.populateHeading(this.headingFragment, true);
          drawerFlyout.populateContent(this.content, true);
          this._bindATCEvent(this.content, drawerFlyout);
        });
    } else {
      drawerFlyout.populateHeading(this.headingFragment, true);
      drawerFlyout.populateContent(this.content, true);
      this._bindATCEvent(this.content, drawerFlyout);
    }
  }

  _bindATCEvent(drawerContent, drawerFlyout) {
    const buyButton = drawerContent.querySelector('[data-component="buy-buttons"]');

    this.events.register(buyButton, 'product:add-to-cart:success', () => {
      this.drawerModal.closeFlyout(drawerFlyout);
      this.loaded = false;
    });
  }

  /**
   *
   * Preloads quickshop for quicker initialization
   *
   * @param {Object} data An object containing data relevant to the quickshop
   * @param {String} data.productLink The link to fetch the product quickshop content
   * @returns {Promise}
   */
  loadQuickshop(data) {
    const { productLink } = data;

    return new Promise(resolve => {
      fetch(productLink.includes('?') ? `${productLink}&view=quickshop` : `${productLink}?view=quickshop`)
        .then(response => response.text()) // Return text string of the HTML
        .then(html => {
          // Convert the HTML string into a document object
          const quickshopComponentEl = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-quickshop]');
          const quickshopSectionEl = quickshopComponentEl.parentElement;
          const quickshopHeadingFragment = quickshopSectionEl
            .querySelector('[data-product-quickshop-heading-template]')
            .content
            .cloneNode(true);

          this.headingFragment = quickshopHeadingFragment;
          this.content = quickshopSectionEl;
          this.loaded = true;
          this.loadedLink = productLink;
          resolve();
        });
    });
  }
}
